import React from 'react';
import styled from '@emotion/styled';
import { useDashboardData } from '../useDashboardData';
import { TileHint } from '../TileFrame';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../dashboardTokens';
import { ClampedText } from './summaries/summariesShared';
import AiAvatar from '../AiAvatar';

// Chat activity (#1160, specs/workshop-dashboard.md): a chat analogue
// (decision 2026-10-19) — the human's question sits left, the beginning of
// the AI's reply right with the AI symbol as avatar. Anonymous, never a
// name. The expanded view is the scrollable list of recent exchanges.

const PairList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 0.9vmin, 20px);
  min-height: 0;
  overflow: hidden;
`;

const Bubble = styled.li`
  display: flex;
  align-items: flex-start;
  gap: clamp(6px, 0.8vmin, 16px);
  max-width: 88%;
  align-self: ${({ $ai }) => ($ai ? 'flex-end' : 'flex-start')};
  padding: clamp(6px, 0.9vmin, 18px) clamp(8px, 1.1vmin, 22px);
  border-radius: clamp(8px, 1vmin, 20px);
  background: ${({ $ai }) => ($ai ? DASH_COLORS.accentSoft : DASH_COLORS.wash)};
  font-size: ${DASH_TYPE.meta};
  color: ${DASH_COLORS.text};

  time {
    font-family: ${DASH_FONTS.numeric};
    color: ${DASH_COLORS.textDim};
    font-size: 0.8em;
    flex: 0 0 auto;
    margin-top: 0.25em;
  }
`;

function Exchange({ q, questionLines, replyLines }) {
  return (
    <>
      <Bubble>
        <time>
          {new Date(q.at).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
        </time>
        <ClampedText lines={questionLines}>{q.content}</ClampedText>
      </Bubble>
      {q.reply && (
        <Bubble $ai>
          <AiAvatar />
          <ClampedText lines={replyLines}>{q.reply}</ClampedText>
        </Bubble>
      )}
    </>
  );
}

export function ChatTileBody({ t }) {
  const { data } = useDashboardData();
  const chat = data?.chat;
  if (!chat) return <TileHint>{t('dashboard.noData')}</TileHint>;
  const latest = (chat.recent || []).slice(0, 2);
  if (!latest.length) return <TileHint>{t('dashboard.noData')}</TileHint>;
  return (
    <>
      <PairList>
        {latest.map((q, i) => (
          <Exchange key={`${q.at}-${i}`} q={q} questionLines={2} replyLines={2} />
        ))}
      </PairList>
      <TileHint>
        {chat.questions} {t('dashboard.units.questions')} · {t('dashboard.chatToday', { count: chat.questionsToday })}
      </TileHint>
    </>
  );
}

export function ChatTileDetail({ t }) {
  const { data } = useDashboardData();
  const chat = data?.chat;
  if (!chat?.recent?.length) return <TileHint>{t('dashboard.noData')}</TileHint>;
  return (
    <PairList>
      {chat.recent.map((q, i) => (
        <Exchange key={`${q.at}-${i}`} q={q} questionLines={4} replyLines={5} />
      ))}
    </PairList>
  );
}
